// src/project/ProjectManager.js
// Orchestrates project lifecycle: new/open/save/export.
// Uses File System Access API for disk I/O,
// SampleStorage for binary audio, ProjectSerializer for state <-> JSON.

import { serialize, deserialize, validateRefs } from './ProjectSerializer.js';
import * as Tone from 'tone';

export class ProjectManager {
  /**
   * @param {object} trackManager
   * @param {object} clipLibrary
   * @param {import('./SampleStorage.js').SampleStorage} sampleStorage
   * @param {import('../instruments/CustomInstrumentRegistry.js').CustomInstrumentRegistry} customRegistry
   * @param {object} engine - AudioEngine singleton
   */
  constructor(trackManager, clipLibrary, sampleStorage, customRegistry, engine) {
    this.tm = trackManager;
    this.clipLibrary = clipLibrary;
    this.sampleStorage = sampleStorage;
    this.customRegistry = customRegistry;
    this.engine = engine;

    this._handle = null;     // FileSystemFileHandle of current save location
    this._dirty = false;
    this._projectName = 'Untitled';
    this._filePath = '';
    this._created = new Date().toISOString();
    this._dirtyListeners = new Set();

    // Subscribe to state changes → mark dirty
    this._unsubs = [];
    this._unsubs.push(trackManager.onChange(() => this.markDirty()));
    this._unsubs.push(clipLibrary.onChange(() => this.markDirty()));
    this._unsubs.push(customRegistry.onChange(() => this.markDirty()));
  }

  get isDirty() { return this._dirty; }
  get projectName() { return this._projectName; }
  get filePath() { return this._filePath || 'Unsaved project'; }

  markDirty() {
    if (!this._dirty) { this._dirty = true; this._emitDirty(true); }
  }

  markClean() {
    if (this._dirty) { this._dirty = false; this._emitDirty(false); }
  }

  onDirtyChange(fn) {
    this._dirtyListeners.add(fn);
    return () => this._dirtyListeners.delete(fn);
  }

  _emitDirty(val) {
    this._dirtyListeners.forEach(fn => fn(val));
  }

  dispose() {
    for (const unsub of this._unsubs) unsub();
    this._unsubs = [];
  }

  /**
   * Create a new empty project. Returns true if user proceeded.
   * Prompts to save if current project is dirty.
   */
  async newProject() {
    if (this._dirty) {
      if (confirm('Save current project?')) {
        const saved = await this.saveProject();
        if (!saved) return false;
      }
    }
    this._clearWorkspace();
    const name = prompt('Project name:', '') || 'Untitled';
    this._projectName = name.trim() || 'Untitled';
    this.engine.setBpm(100);
    if (this.engine.reverb) this.engine.reverb.wet.value = 0.3;
    this.engine.humanizeAmount = 0.012;
    this.engine.velocityJitter = 0.08;
    this._handle = null;
    this._filePath = '';
    this._created = new Date().toISOString();
    this.markClean();
    return true;
  }

  /** Open a .helix file. Returns true on success. */
  async openProject() {
    if (!window.showOpenFilePicker) { alert('Open files requires Chrome or Edge.'); return false; }
    try {
      const [handle] = await window.showOpenFilePicker({
        types: [{ description: 'Helix Project', accept: { 'application/json': ['.helix'] } }]
      });
      this._handle = handle;
      this._filePath = handle.name;

      const file = await handle.getFile();
      const text = await file.text();
      let data;
      try { data = JSON.parse(text); } catch { alert('Invalid project file.'); return false; }

      const parsed = deserialize(data);
      const errors = validateRefs(data);
      if (errors.length > 0) console.warn('Project validation:', errors);

      if (this._dirty) {
        if (confirm('Save current project first?')) await this.saveProject();
      }

      this._restoreState(parsed);

      const missing = await this._checkMissingSamples(parsed.sampleManifest);
      if (missing.length > 0) {
        alert(`Missing ${missing.length} custom sample(s). The instrument won't produce sound until re-imported.`);
      }

      this._projectName = parsed.project.name;
      this._created = parsed.project.created;
      this.markClean();
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false;
      console.error('Open error:', err);
      alert(`Could not open project: ${err.message}`);
      return false;
    }
  }

  /** Save to current handle, or prompt if none. */
  async saveProject() {
    if (this._handle) return this._writeToHandle(this._handle);
    return this.saveProjectAs();
  }

  /** Always prompt for save location. */
  async saveProjectAs() {
    if (!window.showSaveFilePicker) { alert('Save files requires Chrome or Edge.'); return false; }
    try {
      const handle = await window.showSaveFilePicker({
        types: [{ description: 'Helix Project', accept: { 'application/json': ['.helix'] } }],
        suggestedName: `${this._projectName.replace(/[^a-zA-Z0-9_-]/g, '_')}.helix`
      });
      this._handle = handle;
      this._filePath = handle.name;
      return this._writeToHandle(handle);
    } catch (err) {
      if (err.name === 'AbortError') return false;
      console.error('Save As error:', err);
      alert(`Could not save: ${err.message}`);
      return false;
    }
  }

  async _writeToHandle(handle) {
    try {
      const data = serialize(this.tm, this.clipLibrary, this.engine, this._projectName, this.customRegistry, { projectCreated: this._created });
      const writable = await handle.createWritable();
      await writable.write(JSON.stringify(data, null, 2));
      await writable.close();
      this.markClean();
      return true;
    } catch (err) {
      console.error('Write error:', err);
      alert(`Save failed: ${err.message}. Try Save As.`);
      return false;
    }
  }

  /** Export project + custom samples to a folder. */
  async exportWithSamples() {
    if (!window.showDirectoryPicker) { alert('Export folder requires Chrome or Edge.'); return false; }
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
      const data = serialize(this.tm, this.clipLibrary, this.engine, this._projectName, this.customRegistry, { forExport: true });

      // Write project.helix
      const fh = await dirHandle.getFileHandle('project.helix', { create: true });
      const w = await fh.createWritable();
      await w.write(JSON.stringify(data, null, 2));
      await w.close();

      // Write samples/ directory with custom audio files
      const samplesDir = await dirHandle.getDirectoryHandle('samples', { create: true });
      for (const c of this.customRegistry.getAll()) {
        for (const [note, ref] of Object.entries(c.sampleMap)) {
          const blob = await this.sampleStorage.getSample(ref);
          if (!blob) continue;
          const sf = await samplesDir.getFileHandle(`${ref}.wav`, { create: true });
          const sw = await sf.createWritable();
          await sw.write(blob);
          await sw.close();
        }
      }

      alert(`Exported to ${dirHandle.name}/`);
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false;
      console.error('Export error:', err);
      alert(`Export failed: ${err.message}`);
      return false;
    }
  }

  // --- Internal ---

  _clearWorkspace() {
    this.tm.clearAll();
    this.clipLibrary.definitions.length = 0;
    this.clipLibrary._emit();
  }

  _restoreState(parsed) {
    this._clearWorkspace();

    // Engine
    if (parsed.engine.bpm != null) this.engine.setBpm(parsed.engine.bpm);
    if (parsed.engine.reverbWet != null && this.engine.reverb) this.engine.reverb.wet.value = parsed.engine.reverbWet;
    if (parsed.engine.humanizeTiming != null) this.engine.humanizeAmount = parsed.engine.humanizeTiming;
    if (parsed.engine.humanizeVelocity != null) this.engine.velocityJitter = parsed.engine.humanizeVelocity;

    // Register custom instruments
    for (const inst of parsed.instruments) {
      if (inst.type === 'custom') {
        this.customRegistry.register({
          id: inst.id, name: inst.name, sampleMap: inst.sampleMap || {},
          release: inst.release ?? 1.2, color: inst.color || '#888'
        });
      }
    }

    // Restore clip library
    for (const clipDef of parsed.clipLibrary) {
      this.clipLibrary.definitions.push(clipDef);
    }
    if (parsed.clipLibrary.length > 0) this.clipLibrary._emit();

    // Restore tracks asynchronously (instrument loading is async)
    setTimeout(() => this._restoreTracksAsync(parsed.tracks), 0);
  }

  async _restoreTracksAsync(tracksData) {
    for (const t of tracksData) {
      try {
        const track = await this.tm.addTrack({
          instrumentId: t.instrumentRef,
          name: t.name,
          mixer: t.mixer
        });
        track.setNotes(t.notes || []);
        for (const ci of (t.clips || [])) {
          track.addClipInstance(ci.clipId, ci.startTime, ci.repeatCount);
        }
      } catch (err) {
        console.warn(`Failed to restore track "${t.name}":`, err);
      }
    }
  }

  async _checkMissingSamples(manifest) {
    const missing = [];
    for (const entry of manifest) {
      const blob = await this.sampleStorage.getSample(entry.ref);
      if (!blob) missing.push(entry);
    }
    return missing;
  }
}
